import { useState } from 'react';
import type { AdminMember } from '@grc/shared';
import { ErrorLine, NoteLine } from '../../components/Lines';
import { t, type Code } from '../../i18n';

type OneTimePasswordProps = {
  /** "Member added", or "Password reset". */
  title: Code;
  member: AdminMember;
  password: string;
  onDone: () => void;
};

/**
 * A one-time password the server made (D23), shown this once: after adding a member, or after a
 * reset. It lives in the screen's state only, never in the URL, the history or storage, so leaving
 * the screen drops it, and a reset makes a new one. Copy message puts a ready message on the
 * clipboard for a chat with the member, with this site's address (the owner's choice, D25).
 */
export function OneTimePassword({ title, member, password, onDone }: OneTimePasswordProps) {
  const [copied, setCopied] = useState<boolean>();

  async function onCopy() {
    const message = t('oneTime.message', {
      username: member.username,
      password,
      address: window.location.origin,
    });
    try {
      // Missing where the page isn't a secure context, and refused by some browsers.
      const clipboard = navigator.clipboard as Clipboard | undefined;
      if (clipboard === undefined) throw new Error('no clipboard');
      await clipboard.writeText(message);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <>
      <h1>{t(title)}</h1>
      <p>{t('oneTime.give', { name: member.displayName })}</p>
      <dl className="login-card">
        <div>
          <dt>{t('member.username')}</dt>
          <dd>{member.username}</dd>
        </div>
        <div>
          <dt>{t('oneTime.password')}</dt>
          <dd className="login-card-password">{password}</dd>
        </div>
      </dl>
      <p className="muted">{t('oneTime.once')}</p>
      <div className="stack">
        <button type="button" className="button button-secondary" onClick={onCopy}>
          {t('oneTime.copy')}
        </button>
        <NoteLine text={copied === true ? t('oneTime.copied') : undefined} />
        <ErrorLine text={copied === false ? t('oneTime.copyFailed') : undefined} />
        <button type="button" className="button" onClick={onDone}>
          {t('oneTime.done')}
        </button>
      </div>
    </>
  );
}
